import React, {Fragment} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from '@app/admin/titles/movie-night.svg';
import {Link} from 'react-router-dom';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {EditIcon} from '@ui/icons/material/Edit';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {FormattedBytes} from '@ui/i18n/formatted-bytes';
import {FormattedRelativeTime} from '@ui/i18n/formatted-relative-time';
import {Title} from '@app/titles/models/title';
import {useAuth} from '@common/auth/use-auth';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {NfoDialog} from '@app/liens/nfo-dialog';
import {InfoIcon} from '@ui/icons/material/Info';
import {useLinkFiltersData} from '@app/liens/use-link-filters';
import {DownloadNzbDialog} from '@app/nzb/download-nzb-dialog';
import {DownloadIcon} from '@ui/icons/material/Download';
import {Nzb} from '@app/titles/models/nzb';
import {NzbIndexPageFilters} from '@common/http/nzb-filters';
import {FetchLinksDataResponse} from '@common/http/links-filters';
import {EnumLinkQualityByIdChip} from '@ui/i18n/enum-quality-by-id';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {Button} from '@ui/buttons/button';
import {FlagIcon} from '@ui/icons/material/Flag';
import {ReportLinkDialog} from '@app/reports/report-link-dialog';
import {ReportNzbDialog} from '@app/reports/report-nzb-dialog';

export function NzbListDatatablePage({
  title,
  filtersData,
  season,
}: {
  title: Title;
  filtersData: FetchLinksDataResponse;
  season?: number;
}) {
  const {hasPermission, user} = useAuth();
  const filtersDefault = useLinkFiltersData();
  const filterKeyPrefix: string = 'nzb';
  const filters = NzbIndexPageFilters({
    data: filtersData,
    filters: filtersDefault,
  });

  const actions: ColumnConfig<Nzb>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      className: 'pt-0',
      body: lien => (
        <Fragment>
          <DialogTrigger type="modal">
            <Tooltip label={<Trans message="Download" />}>
              <IconButton size="md" className="text-muted">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <DownloadNzbDialog id={lien.id} />
          </DialogTrigger>
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <InfoIcon />
            </IconButton>
            <NfoDialog nfo={lien.nfo} />
          </DialogTrigger>
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <FlagIcon />
            </IconButton>
            <ReportNzbDialog link={lien} modelType={'nzb'} />
          </DialogTrigger>
          {(hasPermission('nzb.update') ||
            (hasPermission('nzb.create') && user?.username == lien.author)) && (
            <Link
              to={`/admin/nzbs/${lien.title_id}/${lien.id}/edit`}
              className="text-muted"
            >
              <Tooltip label={<Trans message="Edit" />}>
                <IconButton size="md">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Link>
          )}
        </Fragment>
      ),
    },
  ];
  const commonColumns: ColumnConfig<Nzb>[] = [
    {
      key: 'size',
      visibleInMode: 'all',
      header: () => <Trans message="Size" />,
      body: lien =>
        lien.size && lien.size > 0 ? <FormattedBytes bytes={lien.size} /> : '-',
    },
    {
      key: 'total_parts',
      visibleInMode: 'regular',
      header: () => <Trans message="Total parts" />,
      body: lien => (
        <ChipList radius="rounded" size="xs">
          <Chip>{lien.total_parts}</Chip>
        </ChipList>
      ),
    },
    {
      key: 'qualite',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="Quality" />,
      body: lien => <EnumLinkQualityByIdChip quality={lien.qualite} />,
    },
    {
      key: 'langues_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Languages" />,
      body: lien => {
        const langues = lien?.langues_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.langues_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'subs_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Subtitles" />,
      body: lien => {
        const langues = lien?.subs_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.subs_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'author',
      visibleInMode: 'regular',
      allowsSorting: false,
      header: () => <Trans message="Uploader" />,
      body: lien => <Link to={`/get-user/${lien.author}`}>{lien.author}</Link>,
    },
    {
      key: 'created_at',
      allowsSorting: true,
      visibleInMode: 'regular',
      header: () => <Trans message="Added" />,
      body: lien =>
        lien.updated_at ? <FormattedRelativeTime date={lien.created_at} /> : '',
    },
  ];

  const NzbListDatatableColumns: ColumnConfig<Nzb>[] = [
    ...commonColumns,
    ...actions,
  ];
  const NzbListDatatableColumnsSerie: ColumnConfig<Nzb>[] = [
    {
      key: 'episode',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="Episode(s)" />,
      body: nzb => (
        <ChipList radius="rounded" size="xs">
          {nzb.full_saison ? (
            <Chip key={'full_saison'} selectable>
              <Trans message={'Full season'} />
            </Chip>
          ) : nzb?.episode ? (
            <Chip key={nzb?.episode} selectable>
              {nzb?.episode}
            </Chip>
          ) : (
            nzb?.episodes?.map(episode => (
              <Chip key={episode} selectable>
                {episode}
              </Chip>
            ))
          )}
        </ChipList>
      ),
    },
    ...commonColumns,
    ...actions,
  ];

  return (
    <DataTablePage
      key={`${title.id}-${season}-nzb`}
      endpoint={'nzb'}
      enableSearch={false}
      filters={filters}
      filterKeyPrefix={filterKeyPrefix}
      pinnedFilters={['qualite', 'langues']}
      queryParams={{title_id: title.id, loader: 'nzbdl', season: season}}
      columns={
        title.is_series ? NzbListDatatableColumnsSerie : NzbListDatatableColumns
      }
      enableSelection={false}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No links have been created yet" />}
          filteringTitle={<Trans message="No matching links" />}
        />
      }
    />
  );
}

function PreviewDialog({remainingLangues}: {remainingLangues: string}) {
  return (
    <Dialog size="sm" className="flex flex-col">
      <DialogBody>{remainingLangues}</DialogBody>
    </Dialog>
  );
}
